<script setup lang="ts">
import { X as CloseIcon } from '@lucide/vue'
import { extent } from 'd3-array'
import { scaleLinear } from 'd3-scale'
import { area, curveMonotoneX, line } from 'd3-shape'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import BaseToolbox from '@/components/ui/BaseToolbox.vue'
import { useEventListener } from '@/composables/useEventListener'
import { isMediumScreen, isSmallScreen } from '@/services/uiService'
import {
  findNearestTrackProfilePoint,
  getTrackProfileBreakdown,
  type TrackProfileAnalyses,
  type TrackProfileAnalysis,
  type TrackProfileLeg,
  type TrackProfileLocationMatch,
  type TrackProfileVisibleRange,
} from '@/utils/trackProfileAnalysis'

defineOptions({ name: 'TrackProfileToolbox' })

// Breakdown bars cover the non-elevation metrics. Gradient tints the chart curve only.
type ProfileBreakdownMetricId = 'surface' | 'wayType'
type ProfileSection = { metricId: ProfileBreakdownMetricId; categoryId: string }
export type TrackProfileSummary = {
  primary: string
  secondary: string
  compact?: string
  metrics?: {
    distance: string
    gain: string
    loss: string
    grade: string
  }
}

const props = withDefaults(
  defineProps<{
    analyses: TrackProfileAnalyses
    cursorDistanceM?: number | null
    isCalculatingRoute: boolean
    activeMetric?: ProfileBreakdownMetricId | null
    highlightedCategoryId?: string | null
    visibleRangeM?: TrackProfileVisibleRange | null
    summary?: TrackProfileSummary | null
    placement?: 'bottom'
    panelClass?: string
    panelBodyClass?: string
    mobileSheet?: boolean
    interactionResetKey?: number
    isCollapsed?: boolean
    initialCollapsed?: boolean
    leftEdgeMargin?: number
    bottomOffset?: number | string
    showResetZoomButton?: boolean
    showRestoreButton?: boolean
    showCloseButton?: boolean
    currentLocation?: TrackProfileLocationMatch | null
    /** Show a spinner in place of the chart while the tour's detail (elevation) is loading. */
    loading?: boolean
  }>(),
  {
    cursorDistanceM: null,
    activeMetric: null,
    highlightedCategoryId: null,
    visibleRangeM: null,
    summary: null,
    placement: 'bottom',
    panelBodyClass: '',
    mobileSheet: false,
    isCollapsed: undefined,
    initialCollapsed: true,
    showResetZoomButton: true,
    showRestoreButton: true,
    showCloseButton: true,
    currentLocation: null,
    loading: false,
  },
)

const emit = defineEmits<{
  'update:cursorDistanceM': [value: number | null]
  'update:visibleRangeM': [value: TrackProfileVisibleRange | null]
  'select:range': [value: TrackProfileVisibleRange]
  'update:isCollapsed': [value: boolean]
  'hover:section': [value: ProfileSection | null]
  'cycle:section': [value: ProfileSection]
  'leave:profile': []
  close: []
}>()

// The elevation chart is always tinted by the gradient analysis.
const gradientAnalysis = computed(() => props.analyses.gradient)

// Whether the elevation chart has enough data to render. Used to decide between the chart and the
// loading spinner while the tour's detail (elevation) is still being fetched.
const hasChartData = computed(
  () => gradientAnalysis.value.hasElevation && gradientAnalysis.value.points.length > 1,
)
const showLoadingState = computed(() => props.loading && !hasChartData.value)

const isCollapsed = ref(props.isCollapsed ?? props.initialCollapsed)
const isVisible = ref(true)
const viewportHeight = ref(typeof window === 'undefined' ? 844 : window.innerHeight)
const interactionScopeRef = ref<HTMLDivElement | null>(null)
const chartContainerRef = ref<HTMLDivElement | null>(null)
const svgRef = ref<SVGSVGElement | null>(null)
const zoomRangeM = ref<TrackProfileVisibleRange | null>(props.visibleRangeM)
const selectionStartDistanceM = ref<number | null>(null)
const selectionEndDistanceM = ref<number | null>(null)
const hoveredSection = ref<ProfileSection | null>(null)

const chartWidth = ref(560)
const chartHeight = computed(() => {
  if (isSmallScreen.value) return 87
  if (isMediumScreen.value) return 95
  return 110
})
const profilePanelMaxWidth = computed(() => {
  if (isSmallScreen.value) return 'calc(100% - 24px)'
  if (isMediumScreen.value) return '760px'
  return '920px'
})
const restoreButtonBottom = computed(() =>
  typeof props.bottomOffset === 'number'
    ? `${props.bottomOffset}px`
    : (props.bottomOffset ?? '12px'),
)
const MIN_ZOOM_RANGE_METERS = 25
const MIN_CHART_WIDTH = 320
const margin = {
  top: 12,
  right: 16,
  bottom: 24,
  left: 16,
}
let chartResizeObserver: ResizeObserver | null = null
let observedChartContainer: HTMLDivElement | null = null
let chartWidthFrameId: number | null = null
const innerWidth = computed(() => Math.max(chartWidth.value - margin.left - margin.right, 1))
const innerHeight = computed(() => chartHeight.value - margin.top - margin.bottom)
const panelClass = computed(() =>
  [
    'overflow-hidden rounded-2xl bg-white/96 shadow-xl',
    isSmallScreen.value ? 'max-h-[35vh]' : 'max-h-[30vh]',
    props.panelClass ?? '',
  ]
    .filter(Boolean)
    .join(' '),
)
const bodyClass = computed(() =>
  [
    isSmallScreen.value
      ? '!space-y-1.5 !px-0 !py-2 max-h-[calc(35vh-72px)] overflow-y-auto overscroll-contain'
      : '!space-y-1.5 !px-0 !py-2 max-h-[calc(30vh-56px)] overflow-y-auto overscroll-contain',
    props.panelBodyClass,
  ]
    .filter(Boolean)
    .join(' '),
)
const headerClass = computed(() =>
  props.summary && isSmallScreen.value ? '!items-start !bg-white/95 !px-3 !py-2' : undefined,
)
const isMobileSheet = computed(() => props.mobileSheet && isSmallScreen.value)

type MobileSheetMode = 'compact' | 'chart' | 'details'
const mobileSheetMode = ref<MobileSheetMode>('compact')
const isMobileSheetDragging = ref(false)
const mobileSheetDragHeight = ref<number | null>(null)
const mobileSheetStartY = ref(0)
const mobileSheetStartHeight = ref(0)
const mobileSheetStartTime = ref(0)
const mobileSheetPointerId = ref<number | null>(null)

const mobileSheetHeights = computed<Record<MobileSheetMode, number>>(() => {
  const height = viewportHeight.value
  return {
    compact: 64,
    chart: Math.min(Math.floor(height * 0.24), 220),
    details: Math.min(Math.floor(height * 0.62), Math.max(430, height - 150)),
  }
})
const mobileSheetHeight = computed(
  () => mobileSheetDragHeight.value ?? mobileSheetHeights.value[mobileSheetMode.value],
)
const mobileSheetStyle = computed(() => ({
  height: `calc(${mobileSheetHeight.value}px + env(safe-area-inset-bottom))`,
  paddingBottom: 'env(safe-area-inset-bottom)',
  transition: isMobileSheetDragging.value
    ? 'none'
    : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))
const mobileSheetShowsChart = computed(
  () =>
    mobileSheetMode.value !== 'compact' ||
    mobileSheetHeight.value > mobileSheetHeights.value.compact + 54,
)
const mobileSheetShowsDetails = computed(
  () =>
    mobileSheetMode.value === 'details' ||
    mobileSheetHeight.value > mobileSheetHeights.value.chart + 58,
)

const clampMobileSheetHeight = (height: number) =>
  Math.max(mobileSheetHeights.value.compact, Math.min(height, mobileSheetHeights.value.details))

const getNearestMobileSheetMode = (height: number): MobileSheetMode => {
  const entries = Object.entries(mobileSheetHeights.value) as Array<[MobileSheetMode, number]>
  return entries.reduce<MobileSheetMode>((nearestMode, [mode, modeHeight]) => {
    const nearestDistance = Math.abs(mobileSheetHeights.value[nearestMode] - height)
    const modeDistance = Math.abs(modeHeight - height)
    return modeDistance < nearestDistance ? mode : nearestMode
  }, 'compact')
}

const startMobileSheetDrag = (event: PointerEvent) => {
  if (!isMobileSheet.value || event.button !== 0) return

  event.preventDefault()
  mobileSheetPointerId.value = event.pointerId
  mobileSheetStartY.value = event.clientY
  mobileSheetStartHeight.value = mobileSheetHeight.value
  mobileSheetStartTime.value = Date.now()
  mobileSheetDragHeight.value = mobileSheetHeight.value
  isMobileSheetDragging.value = true
  const target = event.currentTarget as HTMLElement | null
  target?.setPointerCapture(event.pointerId)
}

const moveMobileSheetDrag = (event: PointerEvent) => {
  if (!isMobileSheetDragging.value || mobileSheetPointerId.value !== event.pointerId) return

  event.preventDefault()
  const deltaY = event.clientY - mobileSheetStartY.value
  mobileSheetDragHeight.value = clampMobileSheetHeight(mobileSheetStartHeight.value - deltaY)
}

const finishMobileSheetDrag = (event: PointerEvent) => {
  if (!isMobileSheetDragging.value || mobileSheetPointerId.value !== event.pointerId) return

  const deltaY = event.clientY - mobileSheetStartY.value
  const deltaTime = Math.max(1, Date.now() - mobileSheetStartTime.value)
  const velocity = Math.abs(deltaY) / deltaTime
  const modes: MobileSheetMode[] = ['compact', 'chart', 'details']
  const currentIndex = modes.indexOf(mobileSheetMode.value)

  let nextMode = getNearestMobileSheetMode(mobileSheetHeight.value)
  if (velocity > 0.35) {
    if (deltaY < 0) nextMode = modes[Math.min(modes.length - 1, currentIndex + 1)]
    else nextMode = modes[Math.max(0, currentIndex - 1)]
  }

  mobileSheetMode.value = nextMode
  mobileSheetDragHeight.value = null
  mobileSheetPointerId.value = null
  isMobileSheetDragging.value = false
  const target = event.currentTarget as HTMLElement | null
  if (target?.hasPointerCapture(event.pointerId)) {
    target.releasePointerCapture(event.pointerId)
  }
}

const totalDistanceM = computed(() => Math.max(gradientAnalysis.value.totalDistanceM, 1))
const visibleDistanceRangeM = computed(
  () => zoomRangeM.value ?? { start: 0, end: totalDistanceM.value },
)

const xScale = computed(() =>
  scaleLinear()
    .domain([visibleDistanceRangeM.value.start, visibleDistanceRangeM.value.end])
    .range([margin.left, margin.left + innerWidth.value]),
)

const elevationDomain = computed<[number, number]>(() => {
  const values = gradientAnalysis.value.points
    .map((point) => point.ele)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  const [minValue, maxValue] = extent(values)
  if (minValue === undefined || maxValue === undefined) {
    return [0, 1]
  }

  const span = Math.max(maxValue - minValue, 20)
  const padding = Math.max(10, span * 0.1)
  return [minValue - padding, maxValue + padding]
})

const yScale = computed(() =>
  scaleLinear()
    .domain(elevationDomain.value)
    .range([margin.top + innerHeight.value, margin.top]),
)

const chartPoints = computed(() =>
  gradientAnalysis.value.points
    .filter((point) => point.ele !== null)
    .filter(
      (point) =>
        point.distanceM >= visibleDistanceRangeM.value.start &&
        point.distanceM <= visibleDistanceRangeM.value.end,
    )
    .map((point) => ({
      x: xScale.value(point.distanceM),
      y: yScale.value(point.ele ?? 0),
      distanceM: point.distanceM,
      ele: point.ele ?? 0,
    })),
)

const visibleLegs = computed(() =>
  gradientAnalysis.value.legs.filter(
    (leg) =>
      leg.endDistanceM >= visibleDistanceRangeM.value.start &&
      leg.startDistanceM <= visibleDistanceRangeM.value.end,
  ),
)

const areaPath = computed(() => {
  if (chartPoints.value.length < 2) return ''
  return (
    area<{ x: number; y: number }>()
      .x((point) => point.x)
      .y0(margin.top + innerHeight.value)
      .y1((point) => point.y)
      .curve(curveMonotoneX)(chartPoints.value) ?? ''
  )
})

const linePath = computed(() => {
  if (chartPoints.value.length < 2) return ''
  return (
    line<{ x: number; y: number }>()
      .x((point) => point.x)
      .y((point) => point.y)
      .curve(curveMonotoneX)(chartPoints.value) ?? ''
  )
})

const activePoint = computed(() =>
  props.cursorDistanceM === null
    ? null
    : findNearestTrackProfilePoint(gradientAnalysis.value.points, props.cursorDistanceM),
)

const findActiveLeg = (analysis: TrackProfileAnalysis): TrackProfileLeg | null => {
  if (analysis.legs.length === 0) return null
  const cursorDistanceM = props.cursorDistanceM
  if (cursorDistanceM === null) return null

  return (
    analysis.legs.find(
      (leg) => cursorDistanceM >= leg.startDistanceM && cursorDistanceM <= leg.endDistanceM,
    ) ?? analysis.legs[analysis.legs.length - 1]
  )
}

// On-hover section readout: distance · elevation · gradient · way type · surface.
const sectionReadout = computed(() => {
  if (!activePoint.value) return null

  const gradientLeg = findActiveLeg(props.analyses.gradient)
  const wayTypeLeg = findActiveLeg(props.analyses.wayType)
  const surfaceLeg = findActiveLeg(props.analyses.surface)

  const chips: string[] = []
  const gradientValue = gradientLeg?.rawValue ?? gradientLeg?.categoryLabel
  if (gradientValue) chips.push(gradientValue)
  if (wayTypeLeg && wayTypeLeg.categoryId !== 'unknown') chips.push(wayTypeLeg.categoryLabel)
  if (surfaceLeg && surfaceLeg.categoryId !== 'unknown') chips.push(surfaceLeg.categoryLabel)

  return {
    distance: formatDistance(activePoint.value.distanceM),
    elevation: activePoint.value.ele !== null ? `${Math.round(activePoint.value.ele)} m` : null,
    chips,
  }
})

const formatPercent = (percent: number) => `${Math.round(percent * 100)}%`

const surfaceBreakdown = computed(() => getTrackProfileBreakdown(props.analyses.surface))
const wayTypeBreakdown = computed(() => getTrackProfileBreakdown(props.analyses.wayType))

const bars = computed(() => [
  {
    metricId: 'surface' as const,
    label: 'Surfaces',
    showList: true,
    breakdown: surfaceBreakdown.value,
  },
  {
    metricId: 'wayType' as const,
    label: 'Way types',
    showList: true,
    breakdown: wayTypeBreakdown.value,
  },
])

const isBreakdownEmpty = (breakdown: ReturnType<typeof getTrackProfileBreakdown>) =>
  breakdown.length === 0 || breakdown.every((item) => item.id === 'unknown')

// The id currently emphasized in a given bar — from live hover, else the clicked focus.
const focusedCategoryFor = (metricId: ProfileBreakdownMetricId): string | null => {
  if (hoveredSection.value?.metricId === metricId) return hoveredSection.value.categoryId
  if (props.activeMetric === metricId) return props.highlightedCategoryId
  return null
}

const cursorX = computed(() =>
  xScale.value(
    activePoint.value
      ? Math.max(
          visibleDistanceRangeM.value.start,
          Math.min(activePoint.value.distanceM, visibleDistanceRangeM.value.end),
        )
      : visibleDistanceRangeM.value.start,
  ),
)

const cursorY = computed(() =>
  activePoint.value && activePoint.value.ele !== null ? yScale.value(activePoint.value.ele) : null,
)

const currentLocationMarker = computed(() => {
  const location = props.currentLocation
  if (!location) return null
  if (
    location.distanceM < visibleDistanceRangeM.value.start ||
    location.distanceM > visibleDistanceRangeM.value.end
  ) {
    return null
  }

  return {
    x: xScale.value(location.distanceM),
    y: location.elevationM === null ? null : yScale.value(location.elevationM),
    distanceText: formatDistance(location.distanceM),
    offsetText: `${Math.round(location.distanceFromTrackM)} m from route`,
  }
})

const selectionRangeM = computed(() => {
  if (selectionStartDistanceM.value === null || selectionEndDistanceM.value === null) return null
  return {
    start: Math.min(selectionStartDistanceM.value, selectionEndDistanceM.value),
    end: Math.max(selectionStartDistanceM.value, selectionEndDistanceM.value),
  }
})

const selectionRect = computed(() => {
  if (!selectionRangeM.value) return null

  return {
    x: xScale.value(selectionRangeM.value.start),
    width: xScale.value(selectionRangeM.value.end) - xScale.value(selectionRangeM.value.start),
  }
})

const formatDistance = (distanceM: number) =>
  distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)} km` : `${Math.round(distanceM)} m`

const formatDistanceKm = (distanceM: number) => `${(distanceM / 1000).toFixed(1)} km`

const updateCursorDistance = (distanceM: number | null) => {
  if (distanceM === null) {
    emit('update:cursorDistanceM', null)
    return
  }

  emit('update:cursorDistanceM', Math.max(0, Math.min(distanceM, totalDistanceM.value)))
}

const getDistanceFromPointer = (
  event: PointerEvent | MouseEvent,
  options?: { clampToChart?: boolean },
) => {
  const svg = svgRef.value
  if (!svg) return null

  const rect = svg.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null

  const relativeX = event.clientX - rect.left
  const relativeY = event.clientY - rect.top
  const normalizedX = (relativeX / rect.width) * chartWidth.value
  const normalizedY = (relativeY / rect.height) * chartHeight.value
  const isWithinChartX = normalizedX >= margin.left && normalizedX <= margin.left + innerWidth.value
  const isWithinChartY = normalizedY >= margin.top && normalizedY <= margin.top + innerHeight.value
  if (!options?.clampToChart && (!isWithinChartX || !isWithinChartY)) {
    return null
  }

  const clampedX = Math.max(margin.left, Math.min(normalizedX, margin.left + innerWidth.value))
  return xScale.value.invert(clampedX)
}

// Touch contacts currently on the chart (pointerId → distance). One finger pans the hover
// cursor; two fingers select a zoom region (the pointer-button drag does this for a mouse).
const touchPoints = new Map<number, number>()
const isTouchPointer = (event: PointerEvent) => event.pointerType !== 'mouse'

const clearSelection = () => {
  selectionStartDistanceM.value = null
  selectionEndDistanceM.value = null
}

const syncTwoFingerSelection = () => {
  const distances = [...touchPoints.values()]
  if (distances.length < 2) return
  selectionStartDistanceM.value = Math.min(...distances)
  selectionEndDistanceM.value = Math.max(...distances)
}

const commitZoomFromSelection = () => {
  const range = selectionRangeM.value
  if (range && range.end - range.start >= MIN_ZOOM_RANGE_METERS) {
    zoomRangeM.value = range
    emit('select:range', range)
  }
  clearSelection()
}

const handleChartPointerDown = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    const distanceM = getDistanceFromPointer(event, { clampToChart: true })
    if (distanceM === null) return
    svgRef.value?.setPointerCapture(event.pointerId)
    touchPoints.set(event.pointerId, distanceM)
    if (touchPoints.size >= 2) {
      syncTwoFingerSelection()
      updateCursorDistance(null)
    } else {
      clearSelection()
      updateCursorDistance(distanceM)
    }
    return
  }

  const distanceM = getDistanceFromPointer(event)
  if (distanceM === null) return

  selectionStartDistanceM.value = distanceM
  selectionEndDistanceM.value = distanceM
  updateCursorDistance(distanceM)
}

const handleChartPointerMove = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    if (!touchPoints.has(event.pointerId)) return
    const distanceM = getDistanceFromPointer(event, { clampToChart: true })
    if (distanceM === null) return
    touchPoints.set(event.pointerId, distanceM)
    if (touchPoints.size >= 2) {
      syncTwoFingerSelection()
      updateCursorDistance(null)
    } else {
      updateCursorDistance(distanceM)
    }
    return
  }

  const isSelecting = selectionStartDistanceM.value !== null && (event.buttons & 1) === 1
  const distanceM = getDistanceFromPointer(event, { clampToChart: isSelecting })
  if (distanceM === null) {
    if (!isSelecting && props.cursorDistanceM !== null && props.cursorDistanceM !== undefined) {
      updateCursorDistance(null)
    }
    return
  }

  if (isSelecting) {
    selectionEndDistanceM.value = distanceM
  }

  updateCursorDistance(distanceM)
}

const handleChartPointerUp = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    const wasTwoFinger = touchPoints.size >= 2
    touchPoints.delete(event.pointerId)
    if (wasTwoFinger) {
      commitZoomFromSelection()
      updateCursorDistance(null)
    } else if (touchPoints.size === 0) {
      clearSelection()
      updateCursorDistance(null)
    }
    return
  }

  const distanceM = getDistanceFromPointer(event, { clampToChart: true })
  if (distanceM !== null) {
    updateCursorDistance(distanceM)
    if (selectionStartDistanceM.value !== null) {
      selectionEndDistanceM.value = distanceM
    }
  }

  commitZoomFromSelection()
}

const handleChartPointerCancel = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    touchPoints.delete(event.pointerId)
    if (touchPoints.size < 2) clearSelection()
    if (touchPoints.size === 0) updateCursorDistance(null)
    return
  }
  clearSelection()
}

const handleChartPointerLeave = (event?: PointerEvent) => {
  // Touch end/cancel is handled explicitly; ignore leave so a finger drifting off the
  // plotted band mid-gesture does not clear the cursor or selection.
  if (event && isTouchPointer(event)) return
  clearSelection()
  updateCursorDistance(null)
}

const resetZoomRange = () => {
  zoomRangeM.value = null
  clearSelection()
}

const enterSection = (metricId: ProfileBreakdownMetricId, categoryId: string) => {
  hoveredSection.value = { metricId, categoryId }
  emit('hover:section', { metricId, categoryId })
}

const leaveSection = () => {
  if (hoveredSection.value === null) return
  hoveredSection.value = null
  emit('hover:section', null)
}

const clickSection = (metricId: ProfileBreakdownMetricId, categoryId: string) => {
  emit('cycle:section', { metricId, categoryId })
}

const leaveProfile = () => {
  leaveSection()
  emit('leave:profile')
}

const resetLegendInteractionState = () => {
  const activeElement = document.activeElement
  if (activeElement instanceof HTMLElement && interactionScopeRef.value?.contains(activeElement)) {
    activeElement.blur()
  }

  leaveSection()
}

const hideProfile = () => {
  leaveProfile()
  isVisible.value = false
  emit('close')
}

const showProfile = () => {
  isVisible.value = true
}

const handleCollapsedUpdate = (value: boolean) => {
  isCollapsed.value = value
  emit('update:isCollapsed', value)
  if (value) {
    leaveProfile()
  }
}

const syncChartWidth = () => {
  const nextWidth = chartContainerRef.value?.clientWidth ?? 0
  if (nextWidth <= 0) return
  chartWidth.value = Math.max(Math.round(nextWidth), MIN_CHART_WIDTH)
}

const scheduleChartWidthSync = (remainingFrames = 4) => {
  if (chartWidthFrameId !== null) {
    window.cancelAnimationFrame(chartWidthFrameId)
  }

  const run = (framesLeft: number) => {
    chartWidthFrameId = window.requestAnimationFrame(() => {
      syncChartWidth()
      if (framesLeft > 1) {
        run(framesLeft - 1)
        return
      }
      chartWidthFrameId = null
    })
  }

  run(remainingFrames)
}

const syncObservedChartContainer = () => {
  if (typeof window === 'undefined' || !('ResizeObserver' in window)) return

  if (!chartResizeObserver) {
    chartResizeObserver = new ResizeObserver(() => {
      scheduleChartWidthSync(6)
    })
  }

  const nextContainer = chartContainerRef.value
  if (observedChartContainer === nextContainer) return

  if (observedChartContainer) {
    chartResizeObserver.unobserve(observedChartContainer)
  }

  observedChartContainer = nextContainer
  if (nextContainer) {
    chartResizeObserver.observe(nextContainer)
    scheduleChartWidthSync(6)
  }
}

watch(totalDistanceM, (distanceM) => {
  if (!zoomRangeM.value) return
  if (zoomRangeM.value.start >= distanceM || zoomRangeM.value.end > distanceM) {
    zoomRangeM.value = null
  }
})

watch(
  () => props.isCollapsed,
  (value) => {
    if (value === undefined) return
    isCollapsed.value = value
  },
)

watch(
  () => props.visibleRangeM,
  (range) => {
    const current = zoomRangeM.value
    if (current?.start === range?.start && current?.end === range?.end) {
      return
    }

    zoomRangeM.value = range
  },
  { deep: true },
)

watch(zoomRangeM, (range) => {
  emit('update:visibleRangeM', range)
})

watch(
  [isVisible, isCollapsed, profilePanelMaxWidth, mobileSheetShowsChart, mobileSheetHeight],
  () => {
    syncObservedChartContainer()
    scheduleChartWidthSync()
  },
  { flush: 'post' },
)

watch(
  chartContainerRef,
  () => {
    syncObservedChartContainer()
  },
  { flush: 'post' },
)

watch(
  () => props.interactionResetKey,
  (nextValue, previousValue) => {
    if (nextValue === previousValue) return
    resetLegendInteractionState()
  },
)

watch(
  () => gradientAnalysis.value.points,
  (points) => {
    if (points.length === 0) {
      emit('update:cursorDistanceM', null)
      resetZoomRange()
    }
  },
  { deep: true },
)

onMounted(() => {
  void nextTick(() => {
    scheduleChartWidthSync(6)
    syncObservedChartContainer()
  })
})

useEventListener(window, 'resize', () => {
  viewportHeight.value = window.innerHeight
  syncObservedChartContainer()
  scheduleChartWidthSync(8)
})

onUnmounted(() => {
  if (chartWidthFrameId !== null) {
    window.cancelAnimationFrame(chartWidthFrameId)
    chartWidthFrameId = null
  }
  chartResizeObserver?.disconnect()
  chartResizeObserver = null
  observedChartContainer = null
})
</script>

<template>
  <button
    v-if="isSmallScreen && !isVisible && props.showRestoreButton"
    type="button"
    class="pointer-events-auto absolute left-3 z-[107] inline-flex h-10 items-center gap-2 rounded-full bg-white/95 px-4 text-sm font-medium text-gray-800 shadow-lg ring-1 ring-black/10 backdrop-blur-sm transition-colors hover:bg-white hover:cursor-pointer"
    :style="{ bottom: restoreButtonBottom }"
    data-testid="track-profile-show"
    @click="showProfile"
  >
    Route profile
  </button>
  <div
    v-if="isVisible && isMobileSheet"
    ref="interactionScopeRef"
    class="pointer-events-auto fixed inset-x-0 bottom-0 z-[107] flex flex-col overflow-hidden rounded-t-2xl border-t border-gray-200 bg-white/96 shadow-2xl backdrop-blur-sm"
    :style="mobileSheetStyle"
    data-testid="track-profile-mobile-sheet"
    @mouseleave="leaveProfile"
  >
    <div
      class="shrink-0 touch-none bg-white/95 px-4 pt-1.5 pb-2"
      :class="{ 'cursor-grabbing': isMobileSheetDragging, 'cursor-grab': !isMobileSheetDragging }"
      data-testid="track-profile-mobile-sheet-handle"
      @pointerdown="startMobileSheetDrag"
      @pointermove="moveMobileSheetDrag"
      @pointerup="finishMobileSheetDrag"
      @pointercancel="finishMobileSheetDrag"
    >
      <div class="mb-1.5 flex justify-center">
        <div class="h-1.5 w-10 rounded-full bg-gray-300"></div>
      </div>
      <div class="flex items-center justify-between gap-2">
        <div class="min-w-0 flex-1">
          <div
            v-if="mobileSheetMode !== 'compact'"
            class="text-sm font-semibold leading-tight text-gray-900"
          >
            Route profile
          </div>
          <div
            v-if="props.summary?.metrics"
            class="grid min-w-0 grid-cols-[minmax(0,1.2fr)_minmax(0,.9fr)_minmax(0,.9fr)_minmax(0,.75fr)] items-baseline gap-x-2 text-[13px] leading-tight"
            :class="mobileSheetMode !== 'compact' ? 'mt-2' : ''"
          >
            <span class="truncate font-semibold text-gray-900">
              A → B {{ props.summary.metrics.distance }}
            </span>
            <span class="truncate font-semibold text-gray-900">
              {{ props.summary.metrics.gain }}
            </span>
            <span class="truncate font-semibold text-gray-900">
              -{{ props.summary.metrics.loss }}
            </span>
            <span class="truncate text-right font-semibold text-gray-900">
              {{ props.summary.metrics.grade }}
            </span>
          </div>
          <div
            v-else-if="props.summary"
            class="flex min-w-0 items-center text-sm font-semibold leading-tight text-gray-900"
            :class="mobileSheetMode !== 'compact' ? 'mt-1' : ''"
          >
            <span class="truncate">{{ props.summary.compact ?? props.summary.primary }}</span>
          </div>
        </div>
        <button
          v-if="props.showCloseButton"
          type="button"
          class="rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close route profile"
          @pointerdown.stop
          @click.stop="hideProfile"
        >
          <CloseIcon class="h-5 w-5" />
        </button>
      </div>
    </div>

    <div
      v-if="mobileSheetShowsChart"
      class="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3"
    >
      <div
        ref="chartContainerRef"
        class="w-full overflow-hidden border-y border-gray-200 bg-white px-0 py-1.5 shadow-sm"
      >
        <svg
          ref="svgRef"
          :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
          class="block h-[97px] w-full cursor-crosshair touch-none"
          data-testid="track-profile-chart"
          @pointerdown="handleChartPointerDown"
          @pointermove="handleChartPointerMove"
          @pointerup="handleChartPointerUp"
          @pointercancel="handleChartPointerCancel"
          @pointerleave="handleChartPointerLeave"
        >
          <defs>
            <linearGradient id="track-profile-mobile-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#bfdbfe" stop-opacity="0.9" />
              <stop offset="100%" stop-color="#eff6ff" stop-opacity="0.5" />
            </linearGradient>
          </defs>

          <path
            v-if="areaPath"
            :d="areaPath"
            fill="url(#track-profile-mobile-fill)"
            stroke="none"
          />
          <path
            v-if="linePath"
            :d="linePath"
            fill="none"
            stroke="#cbd5e1"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="5"
          />

          <rect
            v-if="selectionRect && selectionRect.width > 0"
            :x="selectionRect.x"
            :y="margin.top"
            :width="selectionRect.width"
            :height="innerHeight"
            fill="#3b82f6"
            fill-opacity="0.12"
            stroke="#2563eb"
            stroke-dasharray="3 3"
            stroke-width="1"
          />

          <line
            v-for="leg in visibleLegs"
            :key="`${leg.start.index}-${leg.end.index}-${leg.categoryId}`"
            :x1="xScale(Math.max(visibleDistanceRangeM.start, leg.startDistanceM))"
            :y1="leg.start.ele === null ? margin.top + innerHeight : yScale(leg.start.ele)"
            :x2="xScale(Math.min(visibleDistanceRangeM.end, leg.endDistanceM))"
            :y2="leg.end.ele === null ? margin.top + innerHeight : yScale(leg.end.ele)"
            :stroke="leg.color"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="4"
          />

          <line
            v-if="activePoint"
            :x1="cursorX"
            :x2="cursorX"
            :y1="margin.top"
            :y2="margin.top + innerHeight"
            stroke="#64748b"
            stroke-dasharray="3 3"
            stroke-width="1"
            stroke-opacity="0.55"
          />
          <circle
            v-if="activePoint && cursorY !== null"
            :cx="cursorX"
            :cy="cursorY"
            r="5"
            fill="#ffffff"
            stroke="#64748b"
            stroke-width="1.75"
          />

          <text x="16" :y="chartHeight - 4" class="fill-gray-500 text-[11px]">
            {{ formatDistanceKm(visibleDistanceRangeM.start) }}
          </text>
          <text
            :x="chartWidth - 16"
            :y="chartHeight - 4"
            class="fill-gray-500 text-[11px]"
            text-anchor="end"
          >
            {{ formatDistanceKm(visibleDistanceRangeM.end) }}
          </text>
        </svg>
      </div>

      <div v-if="mobileSheetShowsDetails" class="space-y-3 px-4 pt-3">
        <div v-for="bar in bars" :key="bar.metricId" class="space-y-2">
          <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {{ bar.label }}
          </div>

          <div
            v-if="isBreakdownEmpty(bar.breakdown)"
            class="text-xs leading-5 text-gray-500"
            :data-testid="`track-profile-${bar.metricId}-empty`"
          >
            No {{ bar.label.toLowerCase() }} information available for this route yet.
          </div>

          <template v-else>
            <div
              class="flex h-3 w-full overflow-hidden rounded-full bg-gray-100"
              :data-testid="`track-profile-${bar.metricId}-bar`"
            >
              <button
                v-for="item in bar.breakdown"
                :key="item.id"
                type="button"
                class="h-full min-w-0.5 transition-opacity hover:cursor-pointer"
                :style="{
                  width: `${item.percent * 100}%`,
                  backgroundColor: item.color,
                  opacity:
                    focusedCategoryFor(bar.metricId) === null ||
                    focusedCategoryFor(bar.metricId) === item.id
                      ? 1
                      : 0.35,
                }"
                :disabled="props.isCalculatingRoute"
                :title="`${item.label} · ${formatPercent(item.percent)}`"
                :aria-label="`${bar.label}: ${item.label} ${formatPercent(item.percent)}`"
                @mouseenter="enterSection(bar.metricId, item.id)"
                @mouseleave="leaveSection"
                @focus="enterSection(bar.metricId, item.id)"
                @blur="leaveSection"
                @click="clickSection(bar.metricId, item.id)"
              />
            </div>

            <div v-if="bar.showList" class="space-y-0.5 pt-0.5">
              <button
                v-for="item in bar.breakdown"
                :key="item.id"
                type="button"
                class="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors"
                :class="
                  focusedCategoryFor(bar.metricId) === item.id
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-white/80'
                "
                :disabled="props.isCalculatingRoute"
                :data-testid="`track-profile-${bar.metricId}-legend-${item.id}`"
                @mouseenter="enterSection(bar.metricId, item.id)"
                @mouseleave="leaveSection"
                @focus="enterSection(bar.metricId, item.id)"
                @blur="leaveSection"
                @click="clickSection(bar.metricId, item.id)"
              >
                <span
                  class="h-2.5 w-2.5 rounded-full"
                  :style="{ backgroundColor: item.color }"
                ></span>
                <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
                <span class="tabular-nums text-gray-500">{{ formatDistance(item.distanceM) }}</span>
                <span class="w-10 text-right font-semibold tabular-nums text-gray-900">
                  {{ formatPercent(item.percent) }}
                </span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
  <BaseToolbox
    v-else
    :is-open="isVisible"
    :is-collapsed="isCollapsed"
    :collapsible="true"
    :closeable="isSmallScreen && props.showCloseButton"
    title="Route profile"
    :draggable="false"
    :placement="placement"
    :edge-margin="12"
    :left-edge-margin="leftEdgeMargin"
    :bottom-offset="bottomOffset"
    bottom-align="center"
    :bottom-max-width="profilePanelMaxWidth"
    collapse-on-header-click
    :panel-class="panelClass"
    :header-class="headerClass"
    :body-class="bodyClass"
    data-testid="track-profile-toolbox"
    @update:is-collapsed="handleCollapsedUpdate"
    @close="hideProfile"
    @mouseleave="leaveProfile"
  >
    <template #title>
      <div class="min-w-0">
        <div class="text-sm font-semibold leading-tight text-gray-900">Route profile</div>
        <div
          v-if="props.summary"
          class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-medium leading-tight text-gray-600"
        >
          <span class="font-semibold text-gray-900">{{ props.summary.primary }}</span>
          <span>{{ props.summary.secondary }}</span>
        </div>
      </div>
    </template>

    <div ref="interactionScopeRef" class="space-y-2">
      <div
        v-if="showLoadingState"
        role="status"
        class="flex flex-col items-center justify-center gap-2 py-10 text-sm text-gray-500"
      >
        <BaseSpinner size="md" />
        <span>Loading elevation…</span>
      </div>
      <template v-else>
        <!-- Gradient-tinted elevation chart. Way types / surfaces are shown as bars below. -->
        <div class="flex flex-wrap items-center justify-between gap-2 px-4 text-xs">
          <div class="font-medium text-gray-700" data-testid="track-profile-range-label">
            Range {{ formatDistanceKm(visibleDistanceRangeM.start) }} -
            {{ formatDistanceKm(visibleDistanceRangeM.end) }}
          </div>
          <div class="flex flex-wrap items-center gap-2 text-gray-500">
            <span class="hidden sm:inline">Drag to select A → B and zoom</span>
            <button
              v-if="zoomRangeM && props.showResetZoomButton"
              type="button"
              class="rounded-full border border-gray-300 bg-white px-3 py-0.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:cursor-pointer"
              data-testid="track-profile-reset-zoom"
              @click="resetZoomRange"
            >
              Reset zoom
            </button>
          </div>
        </div>

        <div
          ref="chartContainerRef"
          class="w-full overflow-hidden border-y border-gray-200 bg-white px-0 py-2 shadow-sm"
        >
          <svg
            ref="svgRef"
            :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
            :class="
              isSmallScreen
                ? 'block h-[97px] w-full cursor-crosshair touch-none'
                : isMediumScreen
                  ? 'block h-[105px] w-full cursor-crosshair touch-none'
                  : 'block h-[120px] w-full cursor-crosshair touch-none'
            "
            data-testid="track-profile-chart"
            @pointerdown="handleChartPointerDown"
            @pointermove="handleChartPointerMove"
            @pointerup="handleChartPointerUp"
            @pointercancel="handleChartPointerCancel"
            @pointerleave="handleChartPointerLeave"
          >
            <defs>
              <linearGradient id="track-profile-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#bfdbfe" stop-opacity="0.9" />
                <stop offset="100%" stop-color="#eff6ff" stop-opacity="0.5" />
              </linearGradient>
            </defs>

            <path v-if="areaPath" :d="areaPath" fill="url(#track-profile-fill)" stroke="none" />
            <path
              v-if="linePath"
              :d="linePath"
              fill="none"
              stroke="#cbd5e1"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="5"
            />

            <rect
              v-if="selectionRect && selectionRect.width > 0"
              :x="selectionRect.x"
              :y="margin.top"
              :width="selectionRect.width"
              :height="innerHeight"
              fill="#3b82f6"
              fill-opacity="0.12"
              stroke="#2563eb"
              stroke-dasharray="3 3"
              stroke-width="1"
            />

            <line
              v-for="leg in visibleLegs"
              :key="`${leg.start.index}-${leg.end.index}-${leg.categoryId}`"
              :x1="xScale(Math.max(visibleDistanceRangeM.start, leg.startDistanceM))"
              :y1="leg.start.ele === null ? margin.top + innerHeight : yScale(leg.start.ele)"
              :x2="xScale(Math.min(visibleDistanceRangeM.end, leg.endDistanceM))"
              :y2="leg.end.ele === null ? margin.top + innerHeight : yScale(leg.end.ele)"
              :stroke="leg.color"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="4"
            />

            <line
              v-if="activePoint"
              :x1="cursorX"
              :x2="cursorX"
              :y1="margin.top"
              :y2="margin.top + innerHeight"
              stroke="#64748b"
              stroke-dasharray="3 3"
              stroke-width="1"
              stroke-opacity="0.55"
            />
            <circle
              v-if="activePoint && cursorY !== null"
              :cx="cursorX"
              :cy="cursorY"
              r="5"
              fill="#ffffff"
              stroke="#64748b"
              stroke-width="1.75"
            />

            <g v-if="currentLocationMarker" data-testid="track-profile-current-location">
              <title>
                You are near {{ currentLocationMarker.distanceText }} ·
                {{ currentLocationMarker.offsetText }}
              </title>
              <line
                :x1="currentLocationMarker.x"
                :x2="currentLocationMarker.x"
                :y1="margin.top"
                :y2="margin.top + innerHeight"
                stroke="#94a3b8"
                stroke-dasharray="1.5 5"
                stroke-linecap="round"
                stroke-width="1.5"
                stroke-opacity="0.65"
              />
              <circle
                v-if="currentLocationMarker.y !== null"
                :cx="currentLocationMarker.x"
                :cy="currentLocationMarker.y"
                r="5.5"
                fill="#64748b"
                fill-opacity="0.12"
                stroke="#94a3b8"
                stroke-width="1.5"
              />
              <circle
                v-if="currentLocationMarker.y !== null"
                :cx="currentLocationMarker.x"
                :cy="currentLocationMarker.y"
                r="2.25"
                fill="#64748b"
                stroke="#ffffff"
                stroke-width="1.25"
              />
            </g>

            <text x="16" :y="chartHeight - 4" class="fill-gray-500 text-[11px]">
              {{ formatDistanceKm(visibleDistanceRangeM.start) }}
            </text>
            <text
              :x="chartWidth - 16"
              :y="chartHeight - 4"
              class="fill-gray-500 text-[11px]"
              text-anchor="end"
            >
              {{ formatDistanceKm(visibleDistanceRangeM.end) }}
            </text>
          </svg>
        </div>

        <div
          v-if="sectionReadout"
          class="mx-4 flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
          data-testid="track-profile-readout"
        >
          <span class="font-semibold text-gray-900">{{ sectionReadout.distance }}</span>
          <span v-if="sectionReadout.elevation" class="text-gray-700">{{
            sectionReadout.elevation
          }}</span>
          <span
            v-for="(chip, index) in sectionReadout.chips"
            :key="index"
            class="text-gray-700 before:mr-3 before:text-gray-300 before:content-['·']"
          >
            {{ chip }}
          </span>
        </div>

        <!-- Surfaces and Way types breakdown bars; click a segment / row to jump on the map. -->
        <div v-for="bar in bars" :key="bar.metricId" class="space-y-1 px-4">
          <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {{ bar.label }}
          </div>

          <div
            v-if="isBreakdownEmpty(bar.breakdown)"
            class="text-xs leading-5 text-gray-500"
            :data-testid="`track-profile-${bar.metricId}-empty`"
          >
            No {{ bar.label.toLowerCase() }} information available for this route yet.
          </div>

          <template v-else>
            <div
              class="flex h-3 w-full overflow-hidden rounded-full bg-gray-100"
              :data-testid="`track-profile-${bar.metricId}-bar`"
            >
              <button
                v-for="item in bar.breakdown"
                :key="item.id"
                type="button"
                class="h-full min-w-0.5 transition-opacity hover:cursor-pointer"
                :style="{
                  width: `${item.percent * 100}%`,
                  backgroundColor: item.color,
                  opacity:
                    focusedCategoryFor(bar.metricId) === null ||
                    focusedCategoryFor(bar.metricId) === item.id
                      ? 1
                      : 0.35,
                }"
                :disabled="props.isCalculatingRoute"
                :title="`${item.label} · ${formatPercent(item.percent)}`"
                :aria-label="`${bar.label}: ${item.label} ${formatPercent(item.percent)}`"
                @mouseenter="enterSection(bar.metricId, item.id)"
                @mouseleave="leaveSection"
                @focus="enterSection(bar.metricId, item.id)"
                @blur="leaveSection"
                @click="clickSection(bar.metricId, item.id)"
              />
            </div>

            <div v-if="bar.showList" class="space-y-0.5 pt-0.5">
              <button
                v-for="item in bar.breakdown"
                :key="item.id"
                type="button"
                class="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors"
                :class="
                  focusedCategoryFor(bar.metricId) === item.id
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-white/80'
                "
                :disabled="props.isCalculatingRoute"
                :data-testid="`track-profile-${bar.metricId}-legend-${item.id}`"
                @mouseenter="enterSection(bar.metricId, item.id)"
                @mouseleave="leaveSection"
                @focus="enterSection(bar.metricId, item.id)"
                @blur="leaveSection"
                @click="clickSection(bar.metricId, item.id)"
              >
                <span
                  class="h-2.5 w-2.5 shrink-0 rounded-full"
                  :style="{ backgroundColor: item.color }"
                ></span>
                <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
                <span class="shrink-0 tabular-nums text-gray-500">{{
                  formatDistance(item.distanceM)
                }}</span>
                <span class="w-10 shrink-0 text-right font-semibold tabular-nums text-gray-900">{{
                  formatPercent(item.percent)
                }}</span>
              </button>
            </div>
          </template>
        </div>
      </template>
    </div>
  </BaseToolbox>
</template>
